/**
 * Error Handler Utility
 * Provides consistent error handling across all services
 */

import config from '../supabase/config.js';

/**
 * Custom error class for service-specific errors
 */
export class ServiceError extends Error {
  constructor(message, code, details = null) {
    super(message);
    this.name = 'ServiceError';
    this.code = code;
    this.details = details;
    this.timestamp = new Date().toISOString();
  }
}

/**
 * Error codes for different types of errors
 */
export const ErrorCodes = {
  // Authentication errors
  AUTH_FAILED: 'AUTH_FAILED',
  AUTH_EXPIRED: 'AUTH_EXPIRED',
  AUTH_INVALID: 'AUTH_INVALID',
  
  // Database errors
  DB_CONNECTION_FAILED: 'DB_CONNECTION_FAILED',
  DB_QUERY_FAILED: 'DB_QUERY_FAILED',
  DB_NOT_FOUND: 'DB_NOT_FOUND',
  DB_DUPLICATE: 'DB_DUPLICATE',
  DB_CONSTRAINT_VIOLATION: 'DB_CONSTRAINT_VIOLATION',
  
  // Permission errors
  PERMISSION_DENIED: 'PERMISSION_DENIED',
  INSUFFICIENT_PERMISSIONS: 'INSUFFICIENT_PERMISSIONS',
  
  // Validation errors
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  INVALID_INPUT: 'INVALID_INPUT',
  
  // Tenant errors
  TENANT_REQUIRED: 'TENANT_REQUIRED',
  TENANT_INVALID: 'TENANT_INVALID',
  TENANT_NOT_FOUND: 'TENANT_NOT_FOUND',
  
  // Network errors
  NETWORK_ERROR: 'NETWORK_ERROR',
  TIMEOUT: 'TIMEOUT',
  
  // General errors
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE'
};

/**
 * Handle Supabase errors and convert them to standardized ServiceError
 */
export const handleSupabaseError = (error, context = '') => {
  console.error(`[${context}] Supabase Error:`, error);

  // Handle specific Supabase error codes
  if (error.code) {
    switch (error.code) {
      case 'PGRST116':
        return new ServiceError('Database connection failed', ErrorCodes.DB_CONNECTION_FAILED, error);
      
      case 'PGRST301':
        return new ServiceError('Permission denied', ErrorCodes.PERMISSION_DENIED, error);
      
      case 'PGRST302':
        return new ServiceError('Row not found', ErrorCodes.DB_NOT_FOUND, error);
      
      case '23505': // Unique violation
        return new ServiceError('Duplicate entry found', ErrorCodes.DB_DUPLICATE, error);
      
      case '23503': // Foreign key violation
        return new ServiceError('Referenced record not found', ErrorCodes.DB_CONSTRAINT_VIOLATION, error);
      
      case '23514': // Check violation
        return new ServiceError('Data validation failed', ErrorCodes.VALIDATION_FAILED, error);
      
      default:
        return new ServiceError(`Database error: ${error.message}`, ErrorCodes.DB_QUERY_FAILED, error);
    }
  }

  // Handle authentication errors
  if (error.message?.includes('JWT')) {
    return new ServiceError('Authentication failed', ErrorCodes.AUTH_FAILED, error);
  }

  // Handle network errors
  if (error.message?.includes('fetch')) {
    return new ServiceError('Network connection failed', ErrorCodes.NETWORK_ERROR, error);
  }

  // Handle timeout errors
  if (error.message?.includes('timeout')) {
    return new ServiceError('Request timeout', ErrorCodes.TIMEOUT, error);
  }

  // Default error handling
  return new ServiceError(
    error.message || 'An unknown error occurred',
    ErrorCodes.UNKNOWN_ERROR,
    error
  );
};

/**
 * Handle general errors and provide consistent error response
 */
export const handleError = (error, context = '') => {
  // If it's already a ServiceError, return it
  if (error instanceof ServiceError) {
    return error;
  }

  // If it's a Supabase error, handle it specifically
  if (error?.code || error?.message?.includes('supabase')) {
    return handleSupabaseError(error, context);
  }

  // Handle other types of errors
  console.error(`[${context}] General Error:`, error);

  if (error instanceof Error) {
    return new ServiceError(error.message, ErrorCodes.UNKNOWN_ERROR, error);
  }

  return new ServiceError(
    typeof error === 'string' ? error : 'An unknown error occurred',
    ErrorCodes.UNKNOWN_ERROR,
    error
  );
};

/**
 * Create a standardized error response
 */
export const createErrorResponse = (error, context = '') => {
  const serviceError = handleError(error, context);
  
  return {
    success: false,
    error: {
      message: serviceError.message,
      code: serviceError.code,
      context,
      timestamp: serviceError.timestamp,
      details: config.environment === 'development' ? serviceError.details : undefined
    }
  };
};

/**
 * Create a standardized success response
 */
export const createSuccessResponse = (data, message = 'Success') => {
  return {
    success: true,
    data,
    message,
    timestamp: new Date().toISOString()
  };
};

/**
 * Retry function with exponential backoff
 */
export const retryWithBackoff = async (fn, maxRetries = config.api.retryAttempts, delay = config.api.retryDelay) => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      if (attempt === maxRetries) {
        throw error;
      }
      
      // Don't retry on certain error types
      if (error instanceof ServiceError) {
        switch (error.code) {
          case ErrorCodes.PERMISSION_DENIED:
          case ErrorCodes.AUTH_FAILED:
          case ErrorCodes.VALIDATION_FAILED:
          case ErrorCodes.INVALID_INPUT:
            throw error; // Don't retry these errors
        }
      }
      
      // Wait before retrying (exponential backoff)
      const waitTime = delay * Math.pow(2, attempt - 1);
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
  }
};

/**
 * Validate tenant ID
 */
export const validateTenantId = (tenantId, context = '') => {
  if (!tenantId) {
    throw new ServiceError(
      'Tenant ID is required for this operation',
      ErrorCodes.TENANT_REQUIRED,
      { context, tenantId }
    );
  }

  // Validate tenant ID format (should be like 'tn_01', 'tn_02', etc.)
  if (!/^tn_\d{2}$/.test(tenantId)) {
    throw new ServiceError(
      'Invalid tenant ID format. Expected format: tn_XX',
      ErrorCodes.TENANT_INVALID,
      { context, tenantId }
    );
  }
};

/**
 * Validate required parameters including tenant_id
 */
export const validateRequired = (params, requiredFields, context = '') => {
  const missing = requiredFields.filter(field => !params[field]);
  
  if (missing.length > 0) {
    throw new ServiceError(
      `Missing required fields: ${missing.join(', ')}`,
      ErrorCodes.VALIDATION_FAILED,
      { missing, context }
    );
  }
};

/**
 * Validate required parameters with tenant support
 */
export const validateRequiredWithTenant = (params, requiredFields, context = '', requireTenant = true) => {
  // First validate basic required fields
  validateRequired(params, requiredFields, context);
  
  // Then validate tenant if required
  if (requireTenant && params.tenant_id) {
    validateTenantId(params.tenant_id, context);
  }
};

/**
 * Log error for monitoring (in production, this would send to a logging service)
 */
export const logError = (error, context = '', additionalData = {}) => {
  const logData = {
    timestamp: new Date().toISOString(),
    context,
    error: {
      message: error.message,
      code: error.code || 'UNKNOWN',
      stack: error.stack
    },
    ...additionalData
  };

  if (config.environment === 'development') {
    console.error('Error Log:', logData);
  } else {
    // In production, send to logging service
    console.error('Error occurred:', logData);
  }
};
